import React, { useEffect, useState } from 'react';
import { Star, ExternalLink, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Stars = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < n ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'}`} />
    ))}
  </div>
);

const ReviewCard = ({ r }) => (
  <article
    className="relative bg-slate-800/50 border border-slate-700/50 hover:border-cyan-500/40 rounded-2xl p-6 flex flex-col gap-3 transition-all hover:shadow-xl hover:shadow-cyan-500/8"
    data-testid="google-review-card"
  >
    <Quote className="absolute top-4 right-4 w-8 h-8 text-cyan-500/15" />
    <div className="flex items-center gap-3">
      {r.photo ? (
        <img src={r.photo} alt={r.author} loading="lazy" className="w-11 h-11 rounded-full object-cover border border-slate-600"
          onError={(e) => { e.target.style.display = 'none'; }} />
      ) : (
        <div className="w-11 h-11 rounded-full bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-300 font-bold">
          {(r.author || '?').charAt(0).toUpperCase()}
        </div>
      )}
      <div className="min-w-0">
        <p className="text-white font-semibold text-sm truncate">{r.author || 'Cliente Google'}</p>
        <div className="flex items-center gap-2">
          <Stars n={r.stars || 5} />
          {r.relative_time && <span className="text-slate-500 text-xs">{r.relative_time}</span>}
        </div>
      </div>
    </div>
    <p className="text-slate-300 text-sm leading-relaxed flex-1">{r.text}</p>
    {r.review_uri && (
      <a href={r.review_uri} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 text-xs font-medium transition-colors">
        Ver no Google <ExternalLink className="w-3 h-3" />
      </a>
    )}
  </article>
);

const AvaliacoesGoogle = ({ limit = null, showViewAll = false, showHeading = true }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/google-reviews`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setData(d); })
      .catch(() => {});
  }, []);

  const reviews = (data?.reviews || []).filter(r => r.text);
  if (!reviews.length) return null;
  const shown = limit ? reviews.slice(0, limit) : reviews;

  return (
    <section id="avaliacoes-google" className="py-16 bg-slate-950 relative overflow-hidden" data-testid="google-reviews-section">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-yellow-500 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-blue-500 rounded-full blur-[120px]" />
      </div>
      <div className="container mx-auto px-4 relative z-10">
        {showHeading && (
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-full text-yellow-400 text-sm font-semibold mb-4">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Avaliações reais do Google
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              O que dizem nossos <span className="text-yellow-400">clientes</span>
            </h2>
            {data?.rating && (
              <div className="flex items-center justify-center gap-2 text-slate-300">
                <Stars n={Math.round(data.rating)} />
                <span className="text-white font-bold text-lg" data-testid="reviews-rating-summary">
                  {Number(data.rating).toFixed(1)}
                </span>
                <span className="text-slate-400 text-sm">· {data.review_count} avaliações no Google</span>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {shown.map((r, i) => <ReviewCard key={i} r={r} />)}
        </div>

        {showViewAll && (
          <div className="text-center mt-10">
            <Link
              to="/avaliacoes"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-slate-800 hover:bg-cyan-900/40 border border-slate-700 hover:border-cyan-500/50 text-white font-bold rounded-full transition-all hover:scale-105 text-sm"
              data-testid="ver-todas-avaliacoes-btn"
            >
              Ver todas as avaliações <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default AvaliacoesGoogle;
